/**
 * Canonical 16-byte identity normalization for ADR 0046 route-key v1.
 * UUID (RFC 9562 network order) and ULID (MSB-first) share the same 128-bit layout.
 */

const UUID_HYPHENATED =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_HEX = /^[0-9a-f]{32}$/i;

/** Crockford Base32 alphabet used by ULID (excludes I, L, O, U). */
const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const CROCKFORD_DECODE: Record<string, number> = (() => {
  const table: Record<string, number> = {};
  for (let i = 0; i < CROCKFORD.length; i++) {
    table[CROCKFORD[i]!] = i;
    table[CROCKFORD[i]!.toLowerCase()] = i;
  }
  // Crockford aliases
  table.I = 1;
  table.i = 1;
  table.L = 1;
  table.l = 1;
  table.O = 0;
  table.o = 0;
  return table;
})();

export class IdentityBytesError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "IdentityBytesError";
  }
}

export function assertIdentityBytes(bytes: Uint8Array): Uint8Array {
  if (bytes.byteLength !== 16) {
    throw new IdentityBytesError(
      `Identity must be exactly 16 bytes (128 bits); got ${bytes.byteLength}`,
    );
  }
  return bytes;
}

export function uuidToIdentityBytes(uuid: string): Uint8Array {
  const trimmed = uuid.trim();
  let hex: string;
  if (UUID_HYPHENATED.test(trimmed)) {
    hex = trimmed.replace(/-/g, "");
  } else if (UUID_HEX.test(trimmed)) {
    hex = trimmed;
  } else {
    throw new IdentityBytesError(`Invalid UUID identity: ${uuid}`);
  }
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

export function identityBytesToUuid(bytes: Uint8Array): string {
  assertIdentityBytes(bytes);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function ulidToIdentityBytes(ulid: string): Uint8Array {
  const trimmed = ulid.trim();
  if (trimmed.length !== 26) {
    throw new IdentityBytesError(
      `ULID identity must be exactly 26 characters; got ${trimmed.length}`,
    );
  }
  let value = 0n;
  for (let i = 0; i < 26; i++) {
    const digit = CROCKFORD_DECODE[trimmed[i]!];
    if (digit === undefined) {
      throw new IdentityBytesError(`Invalid ULID identity character at ${i}`);
    }
    value = (value << 5n) | BigInt(digit);
  }
  const bytes = new Uint8Array(16);
  let remaining = value;
  for (let i = 15; i >= 0; i--) {
    bytes[i] = Number(remaining & 0xffn);
    remaining >>= 8n;
  }
  return bytes;
}

export function identityBytesToUlid(bytes: Uint8Array): string {
  assertIdentityBytes(bytes);
  let value = 0n;
  for (const byte of bytes) {
    value = (value << 8n) | BigInt(byte);
  }
  let out = "";
  for (let i = 0; i < 26; i++) {
    const shift = 5n * BigInt(25 - i);
    const index = Number((value >> shift) & 0x1fn);
    out += CROCKFORD[index]!;
  }
  return out;
}
